import axios from 'axios'

// 创建axios实例
// 使用相对路径，自动适配当前部署域名
const instance = axios.create({
  baseURL: '/api', // 相对路径，自动使用当前页面域名
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json;charset=utf-8',
  },
})

// 清除所有用户相关字段
const clearUserData = () => {
  const userFields = [
    'accessToken',
    'expiresIn',
    'isSaas',
    'language',
    'locale',
    'tenantId',
    'theme',
    'tokenType',
    'userId',
    'userInfo'
  ]

  userFields.forEach(field => {
    localStorage.removeItem(field)
  })
}

// 请求拦截器
instance.interceptors.request.use(
  (config) => {

    // 获取token并添加到请求头
    const token = localStorage.getItem('accessToken')
    if (token) {
      config.headers.Authorization = token.startsWith('Bearer ') ? token : `Bearer ${token}`
    }

    // GET请求添加时间戳防止缓存
    if (config.method?.toLowerCase() === 'get' && config.params) {
      config.params._t = Date.now()
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// 响应拦截器
instance.interceptors.response.use(
  (response) => {

    const { config, data, status } = response

    // 打印响应信息
    console.log('✅ 请求成功:', {
      接口: `${config.method?.toUpperCase()} ${config.url}`,
      参数: config.params || config.data || {},
      状态码: status,
      结果: data
    })
    console.log(response)

    // 根据接口文档，响应格式统一为 { code, msg, data }
    if (data && typeof data === 'object' && data.code !== undefined) {
      // 成功状态码：200
      if (data.code === 200) {
        // 特殊处理：对于退出登录接口，返回完整响应数据
        if (config.url && config.url.includes('/Logout')) {
          return data  // 返回完整的 data 对象
        }
        // 其他接口返回 data.data
        return data.data
      }

      // 特殊业务错误码处理
      if (data.code === 600) { // 登录过期
        clearUserData()
        // 跳转到登录页面
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login'
        }
        return Promise.reject(new Error('LOGIN_EXPIRED'))
      }

      if (data.code === 400) {
        // console.log('请求参数错误:', data.msg || '请求参数错误')
      }

      // 其他业务错误
      const message = data.msg || '请求失败'
      return Promise.reject(new Error(message))
    }

    // 如果没有code字段，直接返回数据
    return data



  },
  (error) => {
    if (error.response) {
      const { config, response } = error

      // 打印错误信息
      console.log('❌ 请求失败:', {
        接口: `${config?.method?.toUpperCase()} ${config?.url}`,
        状态码: response.status,
        错误信息: response.data?.msg || response.statusText
      })

      // HTTP状态码错误
      const { status } = response

      if (status === 401 || status === 600) {
        clearUserData()
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login'
        }
      }
    }

    return Promise.reject(error)
  }
)

// 封装请求方法
export const http = {
  // 基础请求方法
  get: (url, params = {}, config = {}) =>
    instance.get(url, { params, ...config }),

  post: (url, data = {}, config = {}) => {
    // 适配FormData
    if (data instanceof FormData) {
      config.headers = {
        ...config.headers,
        'Content-Type': 'multipart/form-data',
      }
    }
    return instance.post(url, data, config)
  },

  put: (url, data = {}, config = {}) =>
    instance.put(url, data, config),

  delete: (url, params = {}, config = {}) =>
    instance.delete(url, { params, ...config }),

  // 文件下载
  download: (url, params = {}, filename = 'download') =>
    instance.get(url, {
      params,
      responseType: 'blob',
    }).then((res) => {
      const blob = new Blob([res.data])
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    }),

  // 检查登录状态
  checkLoginStatus: () => {
    const token = localStorage.getItem('accessToken')
    return !!token && token.trim() !== ''
  },

  // 手动清除用户数据
  clearUserData: () => {
    clearUserData()
  },

  // 获取当前token
  getToken: () => localStorage.getItem('accessToken'),

  // 设置token
  setToken: (token) => {
    if (token) {
      localStorage.setItem('accessToken', token)
    }
  },

  // 获取用户信息
  getUserInfo: () => {
    const userInfoStr = localStorage.getItem('userInfo')
    if (userInfoStr) {
      try {
        return JSON.parse(userInfoStr)
      } catch (e) {
        return null
      }
    }
    return null
  },
}

// 导出axios实例
export default instance

// 导出取消请求工具
export const createCancelToken = () => axios.CancelToken.source()

// 导出清除用户数据函数
export { clearUserData }
